import React from 'react';

import { Skeleton as MUISketelon } from '@mui/material';
import classNames from 'classnames';

import { useStyles } from './Skeleton.styles';

enum SkeletonVariants {
  circle = 'circle',
  rect = 'rect',
  text = 'text',
}

enum MUISkeletonVariants {
  circle = 'circular',
  rect = 'rectangular',
  text = 'text',
}

export interface SkeletonProps {
  variant?: keyof typeof SkeletonVariants;
  width?: number | string;
  height?: number | string;
}

/**
 * Компонент Skeleton, который отображает загрузочные заполнители различных форм и размеров.
 *
 * @param {SkeletonProps} props - Свойства компонента Skeleton.
 * @param {'circle' | 'rect' | 'text'} [props.variant='text'] - Вариант сkeleton ('circle', 'rect' или 'text').
 * @param {number} [props.width] - Ширина сkeleton.
 * @param {number} [props.height] - Высота сkeleton.
 * @param {number} [props.size] - Размер (используется если не указаны width/height)
 * @returns {JSX.Element} - Отрендеренный компонент Skeleton.
 */

export const Skeleton = ({
  variant = 'text',
  width,
  height,
}: SkeletonProps) => {
  const { classes: styles } = useStyles();

  /**
   * Функция getVariantStyles возвращает стили для компонента Skeleton в зависимости от его варианта.
   *
   * @param {keyof typeof SkeletonVariants} skeletonVariant - Вариант сkeleton ('circle', 'rect' или 'text').
   * @returns {string} - CSS-классы для выбранного варианта сkeleton.
   */
  const getVariantStyles = (skeletonVariant: keyof typeof SkeletonVariants) => {
    return styles[`${SkeletonVariants[skeletonVariant]}`];
  };

  /**
   * Функция convertToMUIVariant преобразует пропс 'variant' Skeleton в 'variant' для MUISketelon.
   * @param {keyof typeof SkeletonVariants} skeletonVariant - Вариант ckeleton ('circle', 'rect' или 'text').
   * @returns {MUISkeletonVariants} - Вариант для MUISketelon ('circular', 'rectangular' или 'text').
   */
  const convertToMUIVariant = (
    skeletonVariant: keyof typeof SkeletonVariants,
  ): MUISkeletonVariants => {
    return MUISkeletonVariants[skeletonVariant];
  };

  return (
    <MUISketelon
      className={classNames([styles.body, getVariantStyles(variant)])}
      variant={convertToMUIVariant(variant)}
      width={width}
      height={height}
      data-testid="skeleton-test-id"
    />
  );
};
